import {
  CorrespondenceLanguages,
  PhoneNumberCountries,
} from '@/models/VariousEnum';
import { McsisConstants } from '@/models/constants/McsisConstants';

/**
 * Configuration extracted from .env file for an easy access.
 */
// List of supported SU (e.g: vs, fr)
export const SUPPORTED_SU = process.env.VUE_APP_SUPPORTED_SU.split(McsisConstants.CommaSeparator);

// Configuration regarding app languages/locales
export const SUPPORTED_LANGUAGES = process.env.VUE_APP_SUPPORTED_LANGUAGES.split(McsisConstants.CommaSeparator);

// Base de toutes les route de l'API
export const PUBLIC_ROOT_API = process.env.VUE_APP_PUBLIC_ROOT_API;

// App version used to force refresh the app when new update is available
export const APP_VERSION = process.env.VUE_APP_VERSION.trim();

/**
 * Laguage disponibles pour chaque su
 * @date 17.08.2023 - 11:21:17
 *
 * @type {*}
 */
const CORRESPONDENCE_LANGUAGES_MAPPING: any = {
  vd: [McsisConstants.LanguageFrCode],
  sg: [McsisConstants.LanguageDeCode, McsisConstants.LanguageFrCode],
  tg: [McsisConstants.LanguageDeCode],
  beso: [McsisConstants.LanguageDeCode, McsisConstants.LanguageFrCode],
  bb: [McsisConstants.LanguageDeCode],
  ag: [McsisConstants.LanguageDeCode],
  lu: [McsisConstants.LanguageDeCode],
  ti: [McsisConstants.LanguageItCode],
};

/**
 * Gestion du mappage de la langue pour chaque su
 * @date 17.08.2023 - 11:22:36
 *
 * @export
 * @param {string} suName - Le nom du su
 * @returns {*} - Un tableau qui contient la liste des langue du su qui a �t� pass� en param�tre
 */
export function getCorrespondenceLanguagesBySu(suName: string): any {
  const su: string = suName.toLowerCase();
  if (!CORRESPONDENCE_LANGUAGES_MAPPING.hasOwnProperty(su)) {
    return [];
  }

  const languages = CORRESPONDENCE_LANGUAGES_MAPPING[su];
  const correspondenceLang = [];

  languages.forEach((element: string) => {
    correspondenceLang.push(
      CorrespondenceLanguages[
        element.toUpperCase() as keyof typeof CorrespondenceLanguages
      ],
    );
  });

  return languages;
}

/**
 * Langue par d�faut pour les su qui emploient le site publique
 * @date 16.08.2023 - 13:06:24
 *
 * @type {*}
 */
const FALLBACK_LANGUAGE_MAPPING: any = {
  vd: McsisConstants.LanguageFrCode,
  sg: McsisConstants.LanguageDeCode,
  tg: McsisConstants.LanguageDeCode,
  beso: McsisConstants.LanguageDeCode,
  bb: McsisConstants.LanguageDeCode,
  ag: McsisConstants.LanguageDeCode,
  lu: McsisConstants.LanguageDeCode,
  ti: McsisConstants.LanguageItCode,
};

/**
 * M�thode qui va faire le lien entre le su et la langue par d�faut
 * @date 16.08.2023 - 13:07:22
 *
 * @export
 * @param {string} su -- le su se trouvant dans l'url
 * @returns {string} -- la langue par d�faut du su en question
 */
export function getLanguageFallBackBySu(su: string): string {
  if (!FALLBACK_LANGUAGE_MAPPING.hasOwnProperty(su)) {
    return McsisConstants.EmptyString;
  }

  return FALLBACK_LANGUAGE_MAPPING[su];
}

/**
 * Phone numbers configuration for each SU
 */
const PHONE_NUMBER_COUNTRIES_MAPPING: any = {
  vd: [McsisConstants.SwissCountryCode],
  sg: [McsisConstants.SwissCountryCode],
  tg: [McsisConstants.SwissCountryCode],
  beso: [McsisConstants.SwissCountryCode],
  bb: [McsisConstants.SwissCountryCode],
  ag: [McsisConstants.SwissCountryCode],
  lu: [McsisConstants.SwissCountryCode],
  ti: [McsisConstants.SwissCountryCode],
};

export function getPhoneNumberCountriesBySu(suName: string): string[] {
  const su: string = suName.toLowerCase();
  if (!PHONE_NUMBER_COUNTRIES_MAPPING.hasOwnProperty(su)) {
    return [];
  }

  const countries = PHONE_NUMBER_COUNTRIES_MAPPING[su];
  const phoneNumberCountry = [];

  countries.forEach((element: string) => {
    phoneNumberCountry.push(
      PhoneNumberCountries[
        element.toUpperCase() as keyof typeof PhoneNumberCountries
      ],
    );
  });

  return countries;
}

/**
 * participant can use the platform ath the first rdv
 */
const FIRST_APT_ALLOWED: any = {
  vd: false,
  sg: true,
  tg: true,
  beso: true,
  bb: true,
  ag: true,
  lu: true,
  ti: true,
};

export function isFirstAptAllowed(suName: string) {
  const su: string = suName.toLowerCase();
  if (!FIRST_APT_ALLOWED.hasOwnProperty(su)) {
    return true;
  }
  return FIRST_APT_ALLOWED[su];
}

/**
 * Screening units id and name
 */
const SCREENING_UNITS: any = {
  2: 'vd',
  4: 'sg',
  5: 'tg',
  8: 'beso',
  10: 'ti',
  11: 'bb',
  15: 'lu',
  17: 'ag',
};

export function getScreeningUnitNameFromID(suId: string) {
  const su: number = Number(suId) % 100;
  if (!SCREENING_UNITS.hasOwnProperty(su)) {
    return McsisConstants.EmptyString;
  }
  return SCREENING_UNITS[su];
}

export function getScreeningUnitIdFromName(suName: string): number | null {
  const entry = Object.entries(SCREENING_UNITS).find(
    ([, name]) => name === suName
  );

  return entry ? Number(entry[0]) : null;
}

/**
 * Radiologue choice
 */
const RADIOLOGUE_CHOICE: any = {
  vd: true,
};

export function isRadiologueChoiceActivatedFromSU(suName: string) {
  const su: string = suName.toLowerCase();
  if (!RADIOLOGUE_CHOICE.hasOwnProperty(su)) {
    return false;
  }
  return RADIOLOGUE_CHOICE[su];
}

/**
 * use RDV long canton
 */
const SU_USE_RDV_LONG: any = {
  beso: true,
  bb: true,
  ge: true,
  sg: true,
  vd: true,
  tg: true,
  ag: true,
  lu: true,
  ti: true,
};

export function isRdvLongUsedFromSU(suName: string) {
  const su: string = suName.toLowerCase();
  if (!SU_USE_RDV_LONG.hasOwnProperty(su)) {
    return false;
  }
  return SU_USE_RDV_LONG[su];
}

/**
 * use RDV long center
 */
const EXAM_CENTER_USE_RDV_LONG: any = {};

export function isRdvLongUsedFromCenter(suName: string) {
  const su: string = suName.toLowerCase();
  if (!EXAM_CENTER_USE_RDV_LONG.hasOwnProperty(su)) {
    return false;
  }
  return EXAM_CENTER_USE_RDV_LONG[su];
}

const SU_EDIT_DELETE_ALLOWED: any = {
  beso: true,
  bb: true,
  sg: true,
  vd: true,
  tg: false,
  ag: false,
  lu: true,
  ti: true,
};

export function isEditDeleteAllowed(suName: string) {
  const su: string = suName.toLowerCase();
  if (!SU_EDIT_DELETE_ALLOWED.hasOwnProperty(su)) {
    return false;
  }
  return SU_EDIT_DELETE_ALLOWED[su];
}

const CHANGE_RDV_IN_CLOSED_PERIOD: any = {
  vd: true,
};

export function isEditDeleteAllowedDuringClosedPeriod(suName: string) {
  const su: string = suName.toLowerCase();
  if (!CHANGE_RDV_IN_CLOSED_PERIOD.hasOwnProperty(su)) {
    return false;
  }
  return CHANGE_RDV_IN_CLOSED_PERIOD[su];
}

const CHANGE_RDV_CLOSED_PERIOD_IN_DAYS: any = {
  vd: 13,
};

export function getPeriodTimeAfterAptDuringClosedPeriod(suName: string) {
  const su: string = suName.toLowerCase();
  if (!CHANGE_RDV_CLOSED_PERIOD_IN_DAYS.hasOwnProperty(su)) {
    return 14;
  }
  return CHANGE_RDV_CLOSED_PERIOD_IN_DAYS[su];
}

const SU_FORBID_EDIT_DELETE_X_DAY_BEFORE_RDV: any = {
  beso: 1,
  bb: 3,
  sg: 1,
  vd: 3,
  tg: 3,
  ag: 3,
  lu: 3,
  ti: 3,
};

export function getPeriodTimeBeforeApt(suName: string) {
  const su: string = suName.toLowerCase();
  if (!SU_FORBID_EDIT_DELETE_X_DAY_BEFORE_RDV.hasOwnProperty(su)) {
    return 0;
  }
  return SU_FORBID_EDIT_DELETE_X_DAY_BEFORE_RDV[su];
}

const SU_USE_EQUEST_HOME: any = {
  beso: false,
  bb: true,
  sg: false,
  vd: false,
  tg: true,
  ag: false,
  lu: true,
};

export function useEquestHome(suName: string) {
  const su: string = suName.toLowerCase();
  if (!SU_USE_EQUEST_HOME.hasOwnProperty(su)) {
    return false;
  }
  return SU_USE_EQUEST_HOME[su];
}

const SU_USE_PARTICIPANT_PAGE: any = {
  beso: false,
  bb: false,
  sg: false,
  vd: false,
  tg: true,
  ag: true,
  ti: true,
};

export function useParticipantPage(suName: string) {
  const su: string = suName.toLowerCase();
  if (!SU_USE_PARTICIPANT_PAGE.hasOwnProperty(su)) {
    return false;
  }
  return SU_USE_PARTICIPANT_PAGE[su];
}
